import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// import { useNavigate } from 'react-router-dom';

import { ReactComponent as TransactionIcon } from '../../../assets/icons/sidebar/sidebar-payment.svg';
import { ReactComponent as SlashIcon } from '../../../assets/icons/slash.svg';
import { ReactComponent as StopIcon } from '../../../assets/icons/stop-circle.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { PersonalSessionInterface } from '../../../interface/charging-session.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import {
  disablePersonalSessionFiltersApplied,
  fetchAllPersonalSessions,
  fetchListPersonalSessionFilters,
  updatePersonalSessionFilters,
  setPersonalSessionSortData,
} from '../../../redux/slices/PersonalChargingSessionSlice';
import CustomActionBar from '../../../shared-components/custom-action-bar/CustomActionBar';
import CustomRadioButton from '../../../shared-components/custom-radio-button/CustomRadioButton';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import DeviceSidePanel from '../../../shared-components/table-side-panel/DeviceSidePanel';
import EndConsumerSidePanel from '../../../shared-components/table-side-panel/EndConsumerSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import { getDaysBetween } from '../../../shared-functions/DateTime';
import occpEvent from '../../../utils/api/ocpp-events';

interface ModalInterface {
  title: string;
  header: boolean;
  content: JSX.Element | null;
  rightCorner: string;
  notification: boolean;
}

/**
 * Personal Session Table Component
 * @param {any} param0 - The param0
 * @param {any} param0.currentPage - The currentPage prop
 * @param {any} param0.setCurrentPage - The setCurrentPage prop
 */
function PersonalSessionTable({
  currentPage,
  setCurrentPage,
}: {
  currentPage: string;
  setCurrentPage: (value: string) => void;
}) {
  const { t, i18n } = useTranslation('transaction');
  const dispatch = useAppDispatch();
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [modalView, setModalView] = useState<ModalInterface>({
    title: '',
    content: null,
    header: true,
    rightCorner: '',
    notification: false,
  });
  // @ts-ignore
  const {
    payments: sessions,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
    TotalFinising,
    totalInprocess,
    totalterminated,
    // @ts-ignore
  } = useAppSelector((state) => state.personalChargingSession);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [activeStatus, setActiveStatus] = useState<string>('');
  const isStatusClicked = React.useRef(false);
  // const [isPolling, setIsPolling] = useState<boolean>(false);
  //   const navigate = useNavigate();
  // const { deviceDetails } = useAppSelector((state) => state.tableSidePanel);

  /**
   * Function to handle radio button change
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleAvailabilityChange = (name: string, value: string) => {
    setCurrentPage(value);
  };

  /**
   * Modal box close function
   * @param {any} message - The message
   */
  const showSuccessNotification = (message: string) => {
    if (message) {
      setOpenStatus(true);
      setModalView({
        title: '',
        header: false,
        content: (
          <SuccessNotification
            title={message}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setOpenStatus(false);
            }}
          />
        ),
        rightCorner: '',
        notification: true,
      });
    } else {
      setOpenStatus(false);
    }
  };

  // /**
  //  * Function to open device view model
  //  */
  // const handleOpenModelView = (_: any, newTab: boolean) => {
  //     if (deviceDetails) {
  //         dispatch(
  //             setSelectedDevice({
  //                 id: deviceDetails?.id,
  //                 name: deviceDetails?.chargingPointName,
  //                 chargingPointSn: deviceDetails?.chargingPointSn,
  //                 modelSeries: deviceDetails?.modelSeries,
  //             }),
  //         );

  //         if (newTab) {
  //             const url = `/ev-admin/device-management/device/${deviceDetails.id}/device-details?searchBy=${deviceDetails.chargingPointName}`;
  //             window.open(url, '_blank'); // opens in a new tab
  //         } else {
  //             navigate({
  //                 pathname: `../device-management/device/${deviceDetails.id}/device-details`,
  //                 search: `?searchBy=${deviceDetails.chargingPointName}`,
  //             });
  //         }
  //     } else {
  //         console.log('Error while opening device in separate page');
  //     }
  // };
  /**
   * Fetch the session details in redux
   * @param {any} data - The data
   */
  const fetchSessions = (data: any) => {
    if (getDaysBetween(data.startDate, data.endDate) > 30) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading) {
      dispatch(
        fetchAllPersonalSessions({
          ...data,
          deviceType: 'personal',
          ...(activeStatus ? { status: activeStatus } : {}),
        }),
      );
    }
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchSessions({ ...queryParameters, page, limit, ...sortValue });
  };
  /**
   * Function to handle stop button click
   * @param {any} data - The data
   */
  const handleStop = (data: PersonalSessionInterface | string[]) => {
    let itemsToProcess: PersonalSessionInterface[] = [];

    if (Array.isArray(data)) {
      itemsToProcess = sessions.filter((session) => data.includes(session.id));
    } else {
      itemsToProcess = [data];
    }

    if (itemsToProcess.length === 0) return;

    const promises = itemsToProcess.map((item) => {
      const payload = {
        chargingPointSn: item.chargingPointSn,
        transactionId: item.transactionId,
        userCustomId: item.userCustomId,
        forceStopTransaction: false,
      };
      return occpEvent.remoteStopTransaction(payload);
    });

    Promise.all(promises)
      .then(() => {
        showSuccessNotification(
          itemsToProcess.length > 1
            ? t('transactionsStopRequestSuccess')
            : t('transactionStopRequestSuccess'),
        );
        handleRefresh();
        if (Array.isArray(data)) {
          setSelectedRows([]);
        }
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Function to handle force stop button click
   * @param {any} data - The data
   */
  const handleForceStop = (data: PersonalSessionInterface | string[]) => {
    let itemsToProcess: PersonalSessionInterface[] = [];

    if (Array.isArray(data)) {
      itemsToProcess = sessions.filter((session) => data.includes(session.id));
    } else {
      itemsToProcess = [data];
    }

    if (itemsToProcess.length === 0) return;

    const promises = itemsToProcess.map((item) => {
      const payload = {
        chargingPointSn: item.chargingPointSn,
        transactionId: item.transactionId,
        userCustomId: item.userCustomId,
        forceStopTransaction: true,
      };
      return occpEvent.remoteStopTransaction(payload);
    });

    Promise.all(promises)
      .then(() => {
        showSuccessNotification(
          itemsToProcess.length > 1
            ? t('transactionsForceStopRequestSuccess')
            : t('transactionForceStopRequestSuccess'),
        );
        handleRefresh();
        if (Array.isArray(data)) {
          setSelectedRows([]);
        }
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };
  const column = [
    {
      label: t('chargingStartedAt'),
      accessorKey: 'transactionStartsAt' as keyof PersonalSessionInterface,
      sortAllow: true,
      /**
       * Custom data for the charging started at column
       * @param {any} rowData - The rowData
       * @returns {any} The result
       */
      customData: (rowData: PersonalSessionInterface) => (
        <span>{ConvertUTCtoDate(rowData?.transactionStartsAt as string | any)}</span>
      ),
    },
    {
      label: t('chargingEndedAt'),
      accessorKey: 'transactionEndsAt' as keyof PersonalSessionInterface,
      sortAllow: true,
      /**
       * Custom data for the charging ended at column
       * @param {any} rowData - The rowData
       * @returns {any} The result
       */
      customData: (rowData: PersonalSessionInterface) => (
        <span>{ConvertUTCtoDate(rowData?.transactionEndsAt as string | any)}</span>
      ),
    },
    {
      label: t('transactionId'),
      accessorKey: 'transactionId' as keyof PersonalSessionInterface,
    },
    {
      label: t('endConsumerId'),
      accessorKey: 'userCustomId' as keyof PersonalSessionInterface,
      queryParamsKey: 'userCustomId',
      queryParamsValue: 'userCustomId',
      sidePanelComponent: <EndConsumerSidePanel fetchIdFrom="queryParams" />,
      /**
       * isSidePanelDisabled
       * @param {any} row - The row
       * @returns {any} The result
       */
      isSidePanelDisabled: (row: any) => row.isGuestUser,
    },
    {
      label: t('endConsumerName'),
      accessorKey: 'endConsumer' as keyof PersonalSessionInterface,
      queryParamsKey: 'userCustomId',
      queryParamsValue: 'userCustomId',
      sidePanelComponent: <EndConsumerSidePanel fetchIdFrom="queryParams" />,
      /**
       * isSidePanelDisabled
       * @param {any} row - The row
       * @returns {any} The result
       */
      isSidePanelDisabled: (row: any) => row.isGuestUser,
    },

    {
      label: t('chargerName'),
      accessorKey: 'chargingPointName' as keyof PersonalSessionInterface,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: <DeviceSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('chargerSN'),
      accessorKey: 'chargingPointSn' as keyof PersonalSessionInterface,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: <DeviceSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof PersonalSessionInterface,
      // sortAllow: true,
      /**
       * Custom data for the status column
       * @param {any} rowData - The rowData
       * @returns {any} The result
       */
      customData: (rowData: PersonalSessionInterface) => {
        let statusClass = '';
        let statusKey = 'na';
        if (rowData?.status) {
          const statusLower = rowData.status.toLowerCase();
          if (statusLower === 'finished') {
            statusClass = 'status-finished';
            statusKey = 'finished';
          } else if (
            statusLower === 'inprogress' ||
            statusLower === 'in progress' ||
            statusLower === 'in_progress'
          ) {
            statusClass = 'status-inProgress';
            statusKey = 'inProgress';
          } else if (statusLower === 'pending') {
            statusClass = 'status-pending';
            statusKey = 'pending';
          } else if (statusLower === 'terminated') {
            statusClass = 'status-terminated';
            statusKey = 'terminated';
          } else if (statusLower === 'failed') {
            statusClass = 'status-failed';
            statusKey = 'failed';
          } else {
            statusKey = rowData.status;
          }
        }
        return (
          <span className={`status-badge ${statusClass}`}>
            <span className="status-text capitalize">
              {t(statusKey, { defaultValue: rowData?.status || t('na') })}
            </span>
          </span>
        );
      },
    },
    {
      label: t('actions'),
      accessorKey: 'id' as keyof PersonalSessionInterface,
      /**
       * Custom data for the actions column
       * @param {any} rowData - The rowData
       * @returns {any} The result
       */
      customData: (rowData: PersonalSessionInterface) => {
        if (rowData?.status === 'inProgress') {
          return (
            <div className="stop-buttons">
              <span role="presentation" title={t('stop')}>
                <StopIcon onClick={() => handleStop(rowData)} />
              </span>
              <span role="presentation" title={t('forceStop')}>
                <SlashIcon onClick={() => handleForceStop(rowData)} />
              </span>
            </div>
          );
        }
        return null;
      },
    },
  ];

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchSessions({ ...value, ...queryParameters, ...sortValue });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setPersonalSessionSortData(sortData));
    fetchSessions({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Poll session data every 10 seconds
   */
  // useEffect(() => {
  //   let timeout: NodeJS.Timeout;
  //   if (isPolling) {
  //     timeout = setTimeout(() => {
  //       handleRefresh();
  //       setIsPolling(false);
  //     }, 10000);
  //   }
  //   return () => clearTimeout(timeout);
  // }, [isPolling]);
  useEffect(() => {
    if (isFilterUpdated || sessions.length === 0 || isStatusClicked.current) {
      isStatusClicked.current = false;
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchSessions({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disablePersonalSessionFiltersApplied());
    }
  }, [filters, activeStatus]);

  useEffect(() => {
    dispatch(
      setPageTitle({ title: t('sessions'), icon: <TransactionIcon />, count: totalResults }),
    );
  }, [totalResults, t]);

  /**
   * Check if status filter is currently active
   * @param {string} statusValue - The status value to check
   * @returns {boolean} True if active
   */
  const isStatusActive = (statusValue: string): boolean => {
    return activeStatus === statusValue;
  };

  /**
   * Handle status badge click to apply/toggle filter
   * @param {string} statusValue - The status value to filter by
   */
  const handleStatusClick = (statusValue: string) => {
    isStatusClicked.current = true;
    setActiveStatus((previous) => (previous === statusValue ? '' : statusValue));
  };

  return (
    <div className="payment-management-wrap" dir={i18n.dir()}>
      <ModalBox
        open={openStatus}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setOpenStatus(false)}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <div className="stats-bar">
        <div className="stat-item" key="total">
          <span className="stat-label">{t('total')}</span>
          <span className="stat-value">{totalResults ?? 0}</span>
        </div>
        <div className="stat-divider" />
        <div
          className={`stat-badge finished-badge ${isStatusActive('finished') ? 'active' : ''}`}
          key="finished"
          role="presentation"
          onClick={() => handleStatusClick('finished')}>
          <span className="badge-dot" />
          {t('finished')}: {TotalFinising ?? 0}
        </div>
        <div
          className={`stat-badge inprogress-badge ${isStatusActive('inprogress') ? 'active' : ''}`}
          key="inprogress"
          role="presentation"
          onClick={() => handleStatusClick('inprogress')}>
          <span className="badge-dot" />
          {t('inProgress')}: {totalInprocess ?? 0}
        </div>
        <div
          className={`stat-badge terminated-badge ${isStatusActive('terminated') ? 'active' : ''}`}
          key="terminated"
          role="presentation"
          onClick={() => handleStatusClick('terminated')}>
          <span className="badge-dot" />
          {t('terminated')}: {totalterminated ?? 0}
        </div>
      </div>
      <div className="table-content">
        <CustomTable
          dataKey="PersonalSessions"
          version="v2"
          header={column}
          height={320}
          data={sessions}
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          serialNo
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updatePersonalSessionFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListPersonalSessionFilters}
          rightTopContent={
            <div className="revenue-management-toggle-wrap">
              <CustomRadioButton
                name=""
                value="commercial"
                label={t('commercialCharger')}
                selected={currentPage === 'commercial'}
                onChangeFunc={handleAvailabilityChange}
              />
              <CustomRadioButton
                name=""
                value="personal"
                label={t('personalCharger')}
                selected={currentPage === 'personal'}
                onChangeFunc={handleAvailabilityChange}
              />
            </div>
          }
          refreshRequired
          autoRefreshEnabled
          disableAutoRefreshToggle
          handleRefresh={handleRefresh}
          refershCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          checkbox
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          selectKey="id"
          isRowSelectable={(row) => row.status === 'inProgress'}
        />
        <CustomActionBar
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          actions={[
            {
              label: t('stop'),
              // icon: <StopIcon />,
              buttonType: 'danger-outline',
              /**
               * handle stop
               * @todo
               */
              onClick: () => handleStop(selectedRows),
            },
            {
              label: t('forceStop'),
              // icon: <SlashIcon />,
              buttonType: 'error',
              /**
               * handle force stop
               * @todo
               */
              onClick: () => handleForceStop(selectedRows),
            },
          ]}
        />
      </div>
    </div>
  );
}

export default PersonalSessionTable;
